import {
  Box,
  HStack,
  SimpleGrid,
  VStack,
  VisuallyHidden,
} from "@chakra-ui/react"

import { Tooltip } from "@/components/ui/tooltip"
import { useColorModeValue } from "../../ui/color-mode"

import type { QuizReviewQuestion } from "@/client"
import { useEffect, useMemo, useRef, useState } from "react"
import type { QuizData, QuizSectionSummary } from "./QuizInterfaceContext"
import { useQuizInterface } from "./QuizInterfaceContext"

export type QuestionStatus = "answered" | "unanswered" | "correct" | "incorrect"

export function useNavigatorTriggerCompact() {
  const [isCompact, setIsCompact] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateCompactState = () => {
      const trigger = triggerRef.current
      if (!trigger) return

      const scrollContainer = trigger.closest("main")
      const containerTop = scrollContainer?.getBoundingClientRect().top ?? 0
      setIsCompact(trigger.getBoundingClientRect().top <= containerTop + 48)
    }
    updateCompactState()
    window.addEventListener("scroll", updateCompactState, { passive: true })
    document.addEventListener("scroll", updateCompactState, {
      capture: true,
      passive: true,
    })
    return () => {
      window.removeEventListener("scroll", updateCompactState)
      document.removeEventListener("scroll", updateCompactState, true)
    }
  }, [])

  return { isCompact, triggerRef }
}

interface QuestionNavigatorProps {
  quiz: QuizData
  maxHeightPx?: number
  onNavigate?: () => void
}

const QUESTION_DOT_SIZE = "28px"
const SECTION_DOT_SIZE = "32px"

function SectionDot({
  section,
  sectionNumber,
  isActive,
  canNavigate,
  onSelect,
  testId,
  answeredColor,
  unansweredColor,
  sectionColor,
  ringColor,
}: {
  section: QuizSectionSummary
  sectionNumber: number
  isActive: boolean
  canNavigate: boolean
  onSelect: () => void
  testId?: string
  answeredColor: string
  unansweredColor: string
  sectionColor: string
  ringColor: string
}) {
  const allAnswered = section.answered === section.total
  const borderColor = isActive ? sectionColor : ringColor
  const bg = isActive
    ? sectionColor
    : allAnswered
      ? answeredColor
      : unansweredColor

  const label = `Section ${sectionNumber}: ${section.name} (${section.answered}/${section.total} answered)`
  const textColor = useColorModeValue("gray.900", "white")
  const activeTextColor = useColorModeValue("white", "gray.900")

  return (
    <VStack gap={1} minW="60px" textAlign="center">
      <Tooltip content={label} openDelay={150}>
        <Box
          as="button"
          w={SECTION_DOT_SIZE}
          h={SECTION_DOT_SIZE}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          borderWidth="2px"
          borderColor={borderColor}
          bg={bg}
          color={isActive ? activeTextColor : textColor}
          fontSize="xs"
          fontWeight="semibold"
          lineHeight="1"
          aria-label={label}
          aria-disabled={!canNavigate}
          data-testid={testId}
          onClick={() => {
            if (canNavigate) {
              onSelect()
            }
          }}
          cursor={canNavigate ? "pointer" : "not-allowed"}
          _hover={canNavigate ? { opacity: 0.9 } : undefined}
        >
          {sectionNumber}
          <VisuallyHidden>{label}</VisuallyHidden>
        </Box>
      </Tooltip>
    </VStack>
  )
}

function QuestionDot({
  isCurrent,
  status,
  label,
  questionNumber,
  canNavigate,
  onSelect,
  testId,
  answeredColor,
  unansweredColor,
  currentColor,
  incorrectColor,
  ringColor,
}: {
  isCurrent: boolean
  status: QuestionStatus
  label: string
  questionNumber: number
  canNavigate: boolean
  onSelect: () => void
  testId?: string
  answeredColor: string
  unansweredColor: string
  currentColor: string
  incorrectColor: string
  ringColor: string
}) {
  const bg = isCurrent
    ? currentColor
    : status === "answered"
      ? answeredColor
      : status === "correct"
        ? answeredColor
        : status === "incorrect"
          ? incorrectColor
          : unansweredColor
  const borderColor = isCurrent ? currentColor : ringColor
  const textColor = useColorModeValue("gray.800", "white")
  const activeTextColor = useColorModeValue("white", "gray.900")

  return (
    <Tooltip content={label} openDelay={150}>
      <Box
        as="button"
        w={QUESTION_DOT_SIZE}
        h={QUESTION_DOT_SIZE}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="full"
        borderWidth="2px"
        borderColor={borderColor}
        bg={bg}
        color={
          isCurrent ||
          status === "answered" ||
          status === "correct" ||
          status === "incorrect"
            ? activeTextColor
            : textColor
        }
        fontSize="xs"
        fontWeight="semibold"
        lineHeight="1"
        aria-label={label}
        aria-disabled={!canNavigate}
        data-testid={testId}
        onClick={() => {
          if (canNavigate) {
            onSelect()
          }
        }}
        cursor={canNavigate ? "pointer" : "not-allowed"}
        _hover={canNavigate ? { opacity: 0.9 } : undefined}
      >
        {questionNumber}
      </Box>
    </Tooltip>
  )
}

export function QuestionNavigator({
  quiz,
  maxHeightPx,
  onNavigate,
}: QuestionNavigatorProps) {
  const {
    quizState,
    sections,
    activeSectionId,
    questionStatuses,
    canNavigateQuestions,
    navigateToSection,
    navigateToQuestion,
  } = useQuizInterface()

  const answeredColor = useColorModeValue("green.500", "green.300")
  const unansweredColor = useColorModeValue("gray.300", "gray.600")
  const currentColor = useColorModeValue("blue.500", "blue.200")
  const incorrectColor = useColorModeValue("red.500", "red.300")
  const sectionColor = useColorModeValue("purple.500", "purple.200")
  const ringColor = useColorModeValue("gray.200", "gray.700")

  const hasSections = sections.length > 0
  const activeSection = hasSections
    ? sections.find((section) => section.id === activeSectionId) ?? sections[0]
    : null

  const questionIndexes = hasSections
    ? activeSection?.questionIndexes ?? []
    : quiz.questions.map((_, index) => index)

  if (quiz.questions.length === 0) {
    return null
  }

  const containerMaxH = maxHeightPx ? `${maxHeightPx}px` : undefined

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      borderColor={ringColor}
      maxH={containerMaxH}
      overflowY="auto"
    >
      <VStack gap={3} align="stretch">
        {hasSections && activeSection && (
          <HStack
            gap={1}
            overflowX="auto"
            py={1}
            justify="center"
            flexWrap="wrap"
          >
            {sections.map((section, index) => (
              <SectionDot
                key={section.id}
                section={section}
                sectionNumber={index + 1}
                isActive={section.id === activeSection.id}
                canNavigate={canNavigateQuestions}
                testId={`section-dot-${section.id}`}
                onSelect={() => {
                  navigateToSection(section.id)
                  onNavigate?.()
                }}
                answeredColor={answeredColor}
                unansweredColor={unansweredColor}
                sectionColor={sectionColor}
                ringColor={ringColor}
              />
            ))}
          </HStack>
        )}

        <SimpleGrid columns={{ base: 5, sm: 6, md: 8, lg: 5 }} gap={2} py={1}>
          {questionIndexes.map((questionIndex) => {
            const status: QuestionStatus =
              questionStatuses[questionIndex] ?? "unanswered"
            const questionNumber = questionIndex + 1
            return (
              <QuestionDot
                key={quiz.questions[questionIndex]?.id ?? questionIndex}
                questionNumber={questionNumber}
                label={`Question ${questionNumber} (${status})`}
                status={status}
                isCurrent={questionIndex === quizState.currentQuestionIndex}
                canNavigate={canNavigateQuestions}
                testId={`question-dot-${questionIndex}`}
                onSelect={() => {
                  navigateToQuestion(questionIndex)
                  onNavigate?.()
                }}
                answeredColor={answeredColor}
                unansweredColor={unansweredColor}
                currentColor={currentColor}
                incorrectColor={incorrectColor}
                ringColor={ringColor}
              />
            )
          })}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}

interface ReviewQuestionNavigatorProps {
  questions: QuizReviewQuestion[]
  questionStatuses: QuestionStatus[]
  visibleQuestionIndexes: number[]
  maxHeightPx?: number
  onNavigate?: (questionIndex: number) => void
}

export function ReviewQuestionNavigator({
  questions,
  questionStatuses,
  visibleQuestionIndexes,
  maxHeightPx,
  onNavigate,
}: ReviewQuestionNavigatorProps) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const answeredColor = useColorModeValue("green.500", "green.300")
  const unansweredColor = useColorModeValue("gray.300", "gray.600")
  const incorrectColor = useColorModeValue("red.500", "red.300")
  const currentColor = useColorModeValue("blue.500", "blue.200")
  const sectionColor = useColorModeValue("purple.500", "purple.200")
  const ringColor = useColorModeValue("gray.200", "gray.700")

  const sections = useMemo<QuizSectionSummary[]>(() => {
    const summaryMap = new Map<string, QuizSectionSummary>()
    for (const questionIndex of visibleQuestionIndexes) {
      const section = questions[questionIndex]?.section
      if (!section) continue
      const status = questionStatuses[questionIndex]
      const existing = summaryMap.get(section.id)
      if (existing) {
        existing.total += 1
        existing.questionIndexes.push(questionIndex)
        if (status !== "unanswered") existing.answered += 1
      } else {
        summaryMap.set(section.id, {
          id: section.id,
          name: section.name,
          total: 1,
          answered: status !== "unanswered" ? 1 : 0,
          questionIndexes: [questionIndex],
        })
      }
    }
    return Array.from(summaryMap.values())
  }, [questionStatuses, questions, visibleQuestionIndexes])

  if (questions.length === 0 || visibleQuestionIndexes.length === 0) return null

  const activeSection =
    sections.find((section) => section.id === activeSectionId) ?? sections[0]
  const questionIndexes = sections.length
    ? activeSection?.questionIndexes ?? []
    : visibleQuestionIndexes
  const containerMaxH = maxHeightPx ? `${maxHeightPx}px` : undefined

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      borderColor={ringColor}
      maxH={containerMaxH}
      overflowY="auto"
    >
      <VStack gap={3} align="stretch">
        {sections.length > 0 && activeSection && (
          <HStack
            gap={1}
            overflowX="auto"
            py={1}
            justify="center"
            flexWrap="wrap"
          >
            {sections.map((section, index) => (
              <SectionDot
                key={section.id}
                section={section}
                sectionNumber={index + 1}
                isActive={section.id === activeSection.id}
                canNavigate
                testId={`section-dot-${section.id}`}
                onSelect={() => setActiveSectionId(section.id)}
                answeredColor={answeredColor}
                unansweredColor={unansweredColor}
                sectionColor={sectionColor}
                ringColor={ringColor}
              />
            ))}
          </HStack>
        )}
        <SimpleGrid columns={{ base: 5, sm: 6, md: 8, lg: 5 }} gap={2} py={1}>
          {questionIndexes.map((questionIndex) => {
            const status = questionStatuses[questionIndex] ?? "unanswered"
            const questionNumber = questionIndex + 1
            return (
              <QuestionDot
                key={questions[questionIndex]?.question_id ?? questionIndex}
                questionNumber={questionNumber}
                label={`Question ${questionNumber} (${status})`}
                status={status}
                isCurrent={false}
                canNavigate
                testId={`question-dot-${questionIndex}`}
                onSelect={() => onNavigate?.(questionIndex)}
                answeredColor={answeredColor}
                unansweredColor={unansweredColor}
                currentColor={currentColor}
                incorrectColor={incorrectColor}
                ringColor={ringColor}
              />
            )
          })}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}
